import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth, can } from "@/lib/auth";
import {
  LayoutDashboard, Users, FileText, Receipt, ListChecks, UsersRound,
  Sparkles, Settings as SettingsIcon, LogOut, Menu, X, Globe,
  Landmark, Wallet, Coins, Building2, Hash, MessageCircle, CreditCard
} from "lucide-react";
import Logo from "@/components/Logo";
import TrialBanner from "@/components/TrialBanner";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard", perm: "dashboard_view" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients", perm: "clients_view" },
  { to: "/returns", label: "Tax Returns", icon: FileText, testid: "nav-returns", perm: "returns_view" },
  { to: "/invoices", label: "Invoices", icon: Receipt, testid: "nav-invoices", perm: "invoices_view", altPerm: "invoices_write" },
  { to: "/expenses", label: "Expenses", icon: Wallet, testid: "nav-expenses", perm: "expenses_view" },
  { to: "/banks", label: "Banks", icon: Landmark, testid: "nav-banks", perm: "banks_view" },
  { to: "/cash", label: "Cash & Books", icon: Coins, testid: "nav-cash", perm: "cash_view" },
  { to: "/tasks", label: "Tasks", icon: ListChecks, testid: "nav-tasks", perm: "tasks_view" },
  { to: "/team", label: "Team", icon: UsersRound, testid: "nav-team", adminOnly: true },
  { to: "/portals", label: "Tax Portals", icon: Globe, testid: "nav-portals" },
  { to: "/whatsapp", label: "WhatsApp Connect", icon: MessageCircle, testid: "nav-whatsapp" },
  { to: "/hsn", label: "HSN / SAC Finder", icon: Hash, testid: "nav-hsn", accent: true, perm: "ai" },
  { to: "/ai", label: "AI Assistant", icon: Sparkles, testid: "nav-ai", accent: true, perm: "ai" },
  { to: "/firm", label: "Firm Profile", icon: Building2, testid: "nav-firm", adminOnly: true },
  { to: "/app/billing", label: "Billing", icon: CreditCard, testid: "nav-billing", adminOnly: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex">
      {/* Sidebar — DARK to match brand */}
      <aside
        className={`fixed lg:static top-0 left-0 z-40 h-screen w-64 bg-zinc-950 border-r border-zinc-800 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="sidebar"
      >
        <div className="px-5 py-7 border-b border-zinc-800 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} data-testid="logo" className="block">
            <Logo className="h-12 w-auto" variant="horizontal" />
          </button>
          <button className="lg:hidden text-zinc-400" onClick={() => setOpen(false)} data-testid="sidebar-close">
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin">
          {nav.map((item) => {
            if (item.adminOnly && user?.role !== "admin") return null;
            // Hide nav item if user lacks both view permission AND any alt write permission
            if (item.perm && !can(user, item.perm) && !(item.altPerm && can(user, item.altPerm))) return null;
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? item.accent
                        ? "bg-[#1ABEDC] text-zinc-950"
                        : "bg-white text-zinc-950"
                      : item.accent
                      ? "text-[#1ABEDC] hover:bg-zinc-900"
                      : "text-zinc-300 hover:bg-zinc-900 hover:text-white"
                  }`
                }
              >
                <Icon size={18} strokeWidth={2} />
                <span>{item.label}</span>
                {item.accent && <Sparkles size={12} className="ml-auto opacity-60" />}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-zinc-800 px-4 py-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-[#1ABEDC] text-zinc-950 flex items-center justify-center font-bold font-display">
              {user?.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider">{user?.role}</div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm border border-zinc-700 text-zinc-300 hover:bg-zinc-900 hover:text-white transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {open && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <TrialBanner />
        <header className="bg-zinc-950 border-b border-zinc-800 px-4 py-3 flex items-center gap-4 lg:hidden">
          <button onClick={() => setOpen(true)} data-testid="sidebar-open" className="text-white"><Menu size={22} /></button>
          <Logo className="h-10 w-auto" variant="horizontal" />
        </header>
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
